import styled, { css } from "styled-components";
import { FC, MouseEvent, useState } from "react";
import { DateTime } from "luxon";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";

import DistributionTypeBadge from "./DistributionTypeBadge";
import DistributionStatusBadge from "./DistributionStatusBadge";
import { ColumnTitle, Time } from "./DistributionListItem";
import NetworkIcon from "@/components/NetworkIcon";
import Text from "@/components/Text";
import { ReleaseResource } from "@/types/resources";
import ConditionalRepostBadge from "./ConditionalRepostBadge";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { ReactComponent as InfoIcon } from "@/assets/icons/info-icon.svg";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { numFormatter } from "@/utils/helpers";
import DistributionScheduleDetailsModal from "./DistributionScheduleDetailsModal";
import DistributionScheduleModal from "./DistributionScheduleModal";
import { ReleaseStatusEnum } from "@/utils/enums";
import ConditionalRepostsButton from "./ConditionalRepostsButton";
import EditReleaseInformationForm from "./EditReleaseInformationForm";
import ReleaseTypeBadge from "./ReleaseTypeBadge";

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;

  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const CardContent = styled.div`
  padding: 16px;
  padding-bottom: 8px;
`;

const CardHeading = styled(Text)`
  line-height: 22px;
  margin-bottom: 16px;
`;

const TimeHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const ChannelHolder = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const HiddenInfo = styled.div``;

const HiddenStatsHolder = styled.div`
  display: grid;
  grid-template-columns: 1.55fr 1fr;
  grid-gap: 16px;
  margin-top: 8px;
  padding-bottom: 8px;
`;

const HiddenRepostStatsHolder = styled(HiddenStatsHolder)`
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  padding-top: 16px;
`;

const ShowMoreButtonHolder = styled.div`
  display: flex;
  align-items: center;
`;

interface ShowMoreButtonProps {
  $showHiddenInfo?: boolean;
}

const ShowMoreButton = styled.button<ShowMoreButtonProps>`
  padding: 8px 12px;
  font-family: "Manrope", sans-serif;
  font-weight: 700;
  font-size: 9px;
  line-height: 12px;
  border: 1px solid transparent;
  border-radius: 50px;
  transition: border-color ease-out 0.2s;
  background-color: ${({ theme }) => theme.colors.white};
  svg {
    margin-right: 6px;
    transition: transform ease-out 0.2s;
  }
  &:hover {
    border-color: ${({ theme }) => theme.colors.lightGray};
  }
  ${({ $showHiddenInfo }) =>
    $showHiddenInfo &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
`;

const Break = styled.div`
  height: 1px;
  flex: 1;
  background-color: ${({ theme }) => theme.colors.grayscale};
`;

export const Description = styled.div`
  display: flex;
  align-items: center;
  svg {
    position: absolute;
    top: -2px;
    left: -2px;
  }
`;

export const DescriptionText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ImageWrap = styled.div`
  position: relative;
  display: flex;
`;

const Image = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 8px;
  flex-shrink: 0;
  border: 1px solid rgba(144, 144, 144, 0.3);
`;

const ActionsHolder = styled.div`
  border-radius: 4px 4px 0px 0px;
  background: ${({ theme }) => theme.colors.grayscaleExtraLight};
  padding: 8px 16px;
  text-align: right;
  display: flex;
  justify-content: space-between;
`;

const StyledConditionalRepostsButton = styled(ConditionalRepostsButton)`
  width: 100%;
  justify-content: center;
`;

interface Props {
  release: ReleaseResource;
  onOpenScheduleModal: () => void;
}

const DistributionCalendarModalCard: FC<Props> = ({ release }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [showHiddenInfo, setShowHiddenInfo] = useState(false);

  const [showScheduleUpdateModal, setShowScheduleUpdateModal] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete?`,
        release.title
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(`/v1/releases/${release.id}`);

      queryClient.invalidateQueries(["releases"], {
        exact: false,
      });

      alerts.success("Schedule was successfully deleted");
    } catch (e) {
      alerts.error();
    }
  };

  const onEdit = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowScheduleUpdateModal(true);
  };

  const isFullyEditable = [
    ReleaseStatusEnum.DRAFT,
    ReleaseStatusEnum.PREPARED,
  ].includes(release.status);

  const renderAudienceSegmentationCondition = () => {
    const { conditions } = release;

    if (conditions?.female_percentage) {
      return `Female: ${conditions?.female_percentage}%`;
    }

    if (conditions?.male_percentage) {
      return `Female: ${conditions?.male_percentage}%`;
    }

    return "-";
  };

  return (
    <Card>
      <ActionsHolder>
        <ReleaseTypeBadge isReel={release.isReel} cardView />
        <ActionsDropdown>
          <ActionsDropdownButton
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setShowDetailsModal(true);
            }}
            as="button"
          >
            <span>
              <InfoIcon />
            </span>
            Details
          </ActionsDropdownButton>
          <ActionsDropdownButton as="button" onClick={onEdit}>
            <span>
              <PencilIcon />
            </span>
            Edit
          </ActionsDropdownButton>
          <ActionsDropdownButton onClick={onDelete} as="button">
            <span>
              <TrashIcon />
            </span>
            Delete
          </ActionsDropdownButton>
        </ActionsDropdown>
      </ActionsHolder>
      <CardContent>
        <TimeHolder>
          {!!release.releasedAt && (
            <Time $type="smallBold">
              {DateTime.fromISO(release.releasedAt).toFormat("HH:mm")}
            </Time>
          )}
          <DistributionStatusBadge status={release.status} />
        </TimeHolder>
        <CardHeading $type="caption">{release.title}</CardHeading>
        {!!release.repostsCount && (
          <StyledConditionalRepostsButton releaseId={release.id} />
        )}
        {release.is_repost && (
          <ConditionalRepostBadge hasConditions={!!release.conditions} />
        )}
        <ChannelHolder>
          <Description>
            {!!release.channel?.thumbnail && (
              <ImageWrap>
                <Image
                  src={release.channel.thumbnail}
                  alt={release.channel.name + " thumbnail"}
                />
                <NetworkIcon type={release.channel.network} />
              </ImageWrap>
            )}
            <DescriptionText>
              {!!release.channel && (
                <Text $type="smallHeadline">{release.channel.name}</Text>
              )}
              <Text $type="extraSmall" $color="gray">
                Main
              </Text>
            </DescriptionText>
          </Description>
          {!!release.compilationType && (
            <DistributionTypeBadge type={release.compilationType} />
          )}
        </ChannelHolder>
        <ShowMoreButtonHolder>
          <Break />
          <ShowMoreButton
            $showHiddenInfo={showHiddenInfo}
            onClick={() => setShowHiddenInfo((prev) => !prev)}
          >
            <svg
              width="8"
              height="6"
              viewBox="0 0 8 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.64714 1.65384L4.00009 4.34615L1.35303 1.65384"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {showHiddenInfo ? "Less info" : "More info"}
          </ShowMoreButton>
          <Break />
        </ShowMoreButtonHolder>
        {showHiddenInfo && (
          <HiddenInfo>
            <HiddenStatsHolder>
              <div>
                <ColumnTitle $type="smallHeadline">Fresh ID</ColumnTitle>
                <Text $type="smallMedium" $color="gray">
                  {release.freshId}
                </Text>
              </div>
              <div>
                <ColumnTitle $type="smallHeadline">Project ID</ColumnTitle>
                <Text $type="smallMedium" $color="gray">
                  {release.projectId}
                </Text>
              </div>
              {!!release.author && (
                <div>
                  <ColumnTitle $type="smallHeadline">Set Up By</ColumnTitle>
                  <Text $type="smallMedium" $color="gray">
                    {release.author}
                  </Text>
                </div>
              )}
            </HiddenStatsHolder>
            {!!release.conditions && (
              <HiddenRepostStatsHolder>
                <div>
                  <ColumnTitle $type="smallHeadline">Target Views</ColumnTitle>
                  <Text $type="smallMedium" $color="gray">
                    {release.conditions.views
                      ? numFormatter(parseInt(release.conditions.views))
                      : "-"}
                  </Text>
                </div>
                <div>
                  <ColumnTitle $type="smallHeadline">Target Days</ColumnTitle>
                  <Text $type="smallMedium" $color="gray">
                    {release.conditions.period ?? "-"}
                  </Text>
                </div>
                <div>
                  <ColumnTitle $type="smallHeadline">
                    Target Audience
                  </ColumnTitle>
                  <Text $type="smallMedium" $color="gray">
                    {renderAudienceSegmentationCondition()}
                  </Text>
                </div>
              </HiddenRepostStatsHolder>
            )}
          </HiddenInfo>
        )}
      </CardContent>
      <AnimatePresence>
        {showScheduleUpdateModal &&
          (isFullyEditable ? (
            <DistributionScheduleModal
              editableRelease={release}
              onClose={() => setShowScheduleUpdateModal(false)}
            />
          ) : (
            <EditReleaseInformationForm
              editableRelease={release}
              onClose={() => setShowScheduleUpdateModal(false)}
            />
          ))}
        {showDetailsModal && (
          <DistributionScheduleDetailsModal
            releaseId={release.id}
            onClose={() => setShowDetailsModal(false)}
          />
        )}
      </AnimatePresence>
    </Card>
  );
};

export default DistributionCalendarModalCard;
